import { ADMIN_API_PREFIX, fillPath, route } from "./routes.js";
import { z } from "zod";
import { BLOB_BUCKET_DELETE_AFTER_DAYS_MAX, RESOURCE_SLUG_PATTERN, blobBucketNameSchema, dataLocationSchema } from "@zion/sdk-contracts";
//#region src/blob-buckets.ts
/**
* `/v1/admin/blob-buckets`: the org admin's view and settings of each per-org app bucket of the
* caller's org (2026-10-13-blob-buckets `overview.md` section 2.2, R-069, R-071 item 6), and the
* org admin's request to move the buckets of an app to a new location (R-089 item 2).
*
* The settings live in core-api's `release.org_blob_bucket_settings`, one row per
* `(organization_id, app_slug, logical_name)`. The routes key a bucket by that natural key, so a
* PATCH upserts and no row id travels (R-071 item 6). The control plane creates each bucket; the
* data plane learns that it exists through the operator program (`bound_at`, R-071 item 3).
*
* Producer: core-api's admin blob bucket controller (WS-17). Consumers: the org admin app in
* `witness-ai/zion-admin-app` (WS-18), and core-api's `admin-route-census.spec.ts`, which checks
* each route against `openapi.json`.
*
* Authorization. The controller carries `@RequireOrgPermission('settings:manage')` on the class and
* `NoDelegatedPrincipalGuard`, as `DcrRedirectHostsController` does. The org comes from the
* authenticated principal only. No route takes an org id in its path, query or body. Every read and
* write runs under the caller's org through RLS.
*
* Kernel class (`CONTRACT_ROUTE_KERNEL_CLASS` in `route-census.ts`), right `tenant.settings`, as
* `GET` and `PATCH /v1/admin/org`: the list at tier 0, the PATCH and the relocation request at
* tier 2 (R-089 item 2).
*
* An org admin requests a relocation, and a platform administrator approves both gates of the plan.
* No route here approves or aborts a plan (`00-index.md` stage 4b scope).
*
* Out of this contract (R-069 items 3 and 4): usage, object count and `maxSizeGb`. The strict PATCH
* body refuses a `maxSizeGb` key.
*
* Direction rule (R-28): the request body schema is `z.strictObject` and refuses an unknown key;
* the response schemas are `z.object` and strip one. No schema here carries a `.transform()`, so
* `createOpenApiDocument()` can represent each one.
*/
/** The collection path. It has no parameter, so the OpenAPI form and the request form agree. */
const ADMIN_BLOB_BUCKETS_PATH = `${ADMIN_API_PREFIX}/blob-buckets`;
/** One bucket, in OpenAPI `{param}` form. Fill it with {@link adminBlobBucketPath}. */
const ADMIN_BLOB_BUCKET_PATH_TEMPLATE = `${ADMIN_BLOB_BUCKETS_PATH}/{appSlug}/{logical}`;
/**
* The relocation requests of one bucket, in OpenAPI `{param}` form. Fill it with
* {@link adminBlobBucketRelocationsPath}.
*/
const ADMIN_BLOB_BUCKET_RELOCATIONS_PATH_TEMPLATE = `${ADMIN_BLOB_BUCKET_PATH_TEMPLATE}/relocations`;
/**
* The parameters of {@link UPDATE_BLOB_BUCKET_ROUTE} and {@link REQUEST_BLOB_BUCKET_RELOCATION_ROUTE},
* which core-api binds with `@Param({ schema })`:
*
* - `appSlug`: the app's catalog slug, 2 to 64 characters of `RESOURCE_SLUG_PATTERN` of
*   `@zion/sdk-contracts` (lowercase letters, digits and hyphens, a letter or digit at each end);
* - `logical`: the logical bucket name, `blobBucketNameSchema` of `@zion/sdk-contracts`.
*
* Strict: an unknown key fails. Neither grammar admits `/`, `.` or `..`, so a filled path keeps
* two segments. No `.transform()`.
*/
const blobBucketPathParamsSchema = z.strictObject({
	appSlug: z.string().min(2).max(64).regex(RESOURCE_SLUG_PATTERN, "an app slug"),
	logical: blobBucketNameSchema
});
/**
* `GET /v1/admin/blob-buckets`. Status 200 with {@link listBlobBucketsResponseSchema}: one row per
* allocatable bucket (`scope: org`, `locality: central`) of each app that the caller's org accepted,
* over the same declarations as the control plane planner (R-072 item 2), ordered by `appSlug`, then `logical`, ascending in byte order. A bucket
* with no settings row answers the defaults (`location: null`, `deleteAfterDays: null`,
* `removalGraceDays: 30`). An org with no such bucket gets `[]`. No query. Permission
* `settings:manage`. Kernel class `tenant.settings` tier 0.
*/
const LIST_BLOB_BUCKETS_ROUTE = route("GET", ADMIN_BLOB_BUCKETS_PATH);
/**
* `PATCH /v1/admin/blob-buckets/{appSlug}/{logical}`. Body {@link updateBlobBucketRequestSchema}.
* Status 200 with {@link updateBlobBucketResponseSchema}: the bucket's row after the write. The
* write upserts the settings row of `(caller's org, appSlug, logical)`; a member absent from the
* body keeps its stored value, or the default on a new row. Permission `settings:manage`. Kernel
* class `tenant.settings` tier 2.
*
* Refusals, after the guards (400 bad params or body, 401, 403, the kernel step-up), in this order,
* with the codes of {@link BLOB_BUCKET_ERROR_CODES}:
*
* 1. 404 `blob_bucket_not_found`: the pair is not a row of {@link LIST_BLOB_BUCKETS_ROUTE} for the
*    caller's org. Another org's bucket answers the same, so the route is no existence oracle.
* 2. 409 `blob_bucket_locations_unknown`: the body sets `location` to a value and the data plane
*    holds no allowed list yet (R-071 item 4). `location: null` passes.
* 3. 400 `blob_bucket_location_not_allowed`: the body's `location` is not an entry of the allowed
*    list (R-071 item 6).
* 4. 409 `blob_bucket_location_locked`: the body changes `location` and the bucket exists
*    (`bound_at` is set). A later change is a relocation (R-069 item 5). The stored value again
*    passes.
*
* A 200 that changes `location` while `exists` is `false` stores the pick, and the control plane
* uses it only if it has not yet created the bucket. When the control plane created the bucket
* first, the next facts name a bound location that differs from the pick. core-api then replaces
* the stored pick with the bound location, so the row reads `location` and `effectiveLocation` as
* the place where the bucket lives, with `exists: true`.
* 5. 400 `blob_bucket_retention_shorter`: the body's `deleteAfterDays` is a number below the row's
*    `deleteAfterDaysFloor`, or a number while `deleteAfterDaysFloor` is `null` (R-071 item 6,
*    R-072 item 3). The org may only lengthen the delete age. With no bucket-wide app rule, the
*    bucket keeps its objects with no age, and every age is shorter than that.
*/
const UPDATE_BLOB_BUCKET_ROUTE = route("PATCH", ADMIN_BLOB_BUCKET_PATH_TEMPLATE);
/**
* `POST /v1/admin/blob-buckets/{appSlug}/{logical}/relocations` (R-089 item 2). Body
* {@link requestBlobBucketRelocationRequestSchema}. Status 201 with
* {@link requestBlobBucketRelocationResponseSchema}: the id of the new request. core-api mints the
* id and stores the request in `release.org_blob_relocation_requests` under the caller's org. The
* operator program carries it to the control plane, where provisioning-svc creates a plan that
* moves every app bucket of the org and the app (R-079). A platform administrator approves the
* plan's two gates. Permission `settings:manage`. Kernel class `tenant.settings` tier 2.
*
* Refusals, after the guards (400 bad params or body, 401, 403, the kernel step-up), in this order,
* with the codes of {@link BLOB_BUCKET_ERROR_CODES} and {@link BLOB_BUCKET_RELOCATION_ERROR_CODES}:
*
* 1. 404 `blob_bucket_not_found`: the pair is not a row of {@link LIST_BLOB_BUCKETS_ROUTE} for the
*    caller's org. Another org's bucket answers the same, so the route is no existence oracle.
* 2. 409 `relocation_bucket_not_created`: the bucket does not exist yet (`exists` is `false`). The
*    org admin changes its location with {@link UPDATE_BLOB_BUCKET_ROUTE} in place of a move.
* 3. 400 `relocation_location_not_allowed`: `targetLocation` is not an entry of the data plane's
*    allowed list.
* 4. 409 `relocation_same_location`: `targetLocation` equals the bucket's `effectiveLocation`.
* 5. 409 `relocation_request_open`: the caller's org holds an open request of the app, or the last
*    stored state set holds an open row of the org and the app (`BLOB_RELOCATION_OPEN_STATES` of
*    `@zion/sdk-cp-dp-contracts`). One org and one app have at most one open request. An open
*    request that the control plane never answered does not block after 7 days
*    (`BLOB_RELOCATION_REQUEST_UNANSWERED_DAYS` of core-api, R-092 item 1): no stored state row
*    names its `requestId`, and its `requested_at` is more than 7 days old. In the same
*    transaction core-api closes that request with `closed_state` `refused` and `closed_refusal`
*    `relocation_request_unanswered`, then stores the new request.
*
* Each 201 stores a new request. A repeat of the same body after a 201 answers refusal 5 while the
* first request is open and answered, or open and at most 7 days old.
*/
const REQUEST_BLOB_BUCKET_RELOCATION_ROUTE = route("POST", ADMIN_BLOB_BUCKET_RELOCATIONS_PATH_TEMPLATE);
/**
* Fill {@link ADMIN_BLOB_BUCKET_PATH_TEMPLATE} with one bucket.
*
* Who calls it: the org admin app and core-api's tests.
*
* @param appSlug the app's catalog slug, as {@link blobBucketSchema} `appSlug` publishes it.
* @param logical the logical bucket name, as {@link blobBucketSchema} `logical` publishes it.
* @returns the request path, for example `/v1/admin/blob-buckets/sdk-smoke/uploads`.
* @throws a `ZodError` when either value fails {@link blobBucketPathParamsSchema}, so `..`, a slash
*   or an empty string never reaches a URL. Pure.
*/
function adminBlobBucketPath(appSlug, logical) {
	return fillPath(ADMIN_BLOB_BUCKET_PATH_TEMPLATE, {
		appSlug,
		logical
	}, {
		appSlug: blobBucketPathParamsSchema.shape.appSlug,
		logical: blobBucketPathParamsSchema.shape.logical
	});
}
/**
* Fill {@link ADMIN_BLOB_BUCKET_RELOCATIONS_PATH_TEMPLATE} with one bucket.
*
* Who calls it: the org admin app and core-api's tests.
*
* @param appSlug the app's catalog slug, as {@link blobBucketSchema} `appSlug` publishes it.
* @param logical the logical bucket name, as {@link blobBucketSchema} `logical` publishes it.
* @returns the request path, for example `/v1/admin/blob-buckets/sdk-smoke/uploads/relocations`.
* @throws a `ZodError` when either value fails {@link blobBucketPathParamsSchema}, so `..`, a slash
*   or an empty string never reaches a URL. Pure.
*/
function adminBlobBucketRelocationsPath(appSlug, logical) {
	return fillPath(ADMIN_BLOB_BUCKET_RELOCATIONS_PATH_TEMPLATE, {
		appSlug,
		logical
	}, {
		appSlug: blobBucketPathParamsSchema.shape.appSlug,
		logical: blobBucketPathParamsSchema.shape.logical
	});
}
/**
* The relocation of one bucket as the org admin sees it: the `relocation` member of
* {@link blobBucketSchema} (R-089 item 3). core-api derives it from its own requests and from the
* last state set that the operator program put (`PUT /internal/v1/blob-relocation-states`).
*
* - `state`: `requested`, `refused`, or a plan state (`pending`, `running`, `waiting_approval`,
*   `aborting`, `aborted`, `failed`, `done`), the values of `BLOB_RELOCATION_STATE_VALUES` of
*   `@zion/sdk-cp-dp-contracts`;
* - `waitingGate`: `pause` or `delete_source` while `state` is `waiting_approval`, else `null`;
* - `targetLocation`: the location of the request or the plan;
* - `refusal`: the refusal code of the control plane while `state` is `refused`, else `null`.
*
* The read path renders stored values, so the text members carry no grammar here.
*/
const blobBucketRelocationSchema = z.object({
	state: z.string().min(1),
	waitingGate: z.string().min(1).nullable(),
	targetLocation: z.string().min(1),
	refusal: z.string().min(1).nullable()
});
/**
* One per-org app bucket of the caller's org, with its settings:
*
* - `appSlug`: the app's catalog slug, a non-empty text;
* - `logical`: the logical bucket name the app declares, a non-empty text;
* - `effectiveLocation`: where the bucket lives or will live. For a bucket that exists, it is the
*   bound location, the `location` of the bucket's fact on `PUT /internal/v1/blob-bucket-facts`,
*   and it stays that value after a move of the org data location (the bucket does not move, R-065
*   item 3). For a bucket that does not exist yet, it is `location` when the org picked one, else
*   the org data location that the data plane received from the control plane, else `null` while
*   the data plane holds no such fact;
* - `location`: the org's pick, or `null` for no pick;
* - `deleteAfterDays`: the org's bucket-wide delete age in days, or `null` for no org override;
* - `deleteAfterDaysFloor`: the shortest `deleteAfterDays` that {@link UPDATE_BLOB_BUCKET_ROUTE}
*   accepts, the longest delete age of any lifecycle rule the app declares for the bucket. `null`
*   when the app declares no bucket-wide (unprefixed) rule: the route then refuses every number
*   (refusal 5) and accepts only `null` (R-072 item 3);
* - `removalGraceDays`: the days after the org removes the app before the platform deletes the
*   bucket; 30 when the org set none;
* - `exists`: `true` once the control plane created the bucket and the data plane learned it
*   (`bound_at` is set). After that, `location` cannot change.
* - `relocation`: the relocation of the bucket's app for the caller's org
*   ({@link blobBucketRelocationSchema}), or `null`. A plan moves every app bucket of the org and
*   the app (R-079), so every row of the app carries the same member. core-api takes the first
*   that applies:
*   1. the open row of the org and the app in the last stored state set;
*   2. else the open request of the org and the app with no row in that set: `state: 'requested'`,
*      `waitingGate: null`, `refusal: null` and the request's target;
*   3. else the last state of the newest closed request of the org and the app;
*   4. else `null`.
*
* The read path renders stored values, so the text members carry no grammar here.
*/
const blobBucketSchema = z.object({
	appSlug: z.string().min(1),
	logical: z.string().min(1),
	effectiveLocation: z.string().min(1).nullable(),
	location: z.string().min(1).nullable(),
	deleteAfterDays: z.number().int().nullable(),
	deleteAfterDaysFloor: z.number().int().nullable(),
	removalGraceDays: z.number().int(),
	exists: z.boolean(),
	relocation: blobBucketRelocationSchema.nullable()
});
/** The 200 body of {@link LIST_BLOB_BUCKETS_ROUTE}: every row, unpaged. */
const listBlobBucketsResponseSchema = z.array(blobBucketSchema);
/**
* The body of {@link UPDATE_BLOB_BUCKET_ROUTE}: `{ location?, deleteAfterDays?, removalGraceDays? }`,
* at least one member.
*
* - `location`: a single region (`dataLocationSchema` of `@zion/sdk-contracts`), or `null` to clear
*   the pick so the bucket gets the org data location.
* - `deleteAfterDays`: an integer from 1 to `BLOB_BUCKET_DELETE_AFTER_DAYS_MAX` (3650), or `null` to
*   clear the org override, so the bucket gets the app's declared delete age again (none when the
*   app declares none). `null` is never shorter than the app's age, so refusal 5 never fires on it.
* - `removalGraceDays`: an integer from 1 to 3650.
*
* Refusals, each a 400 from core-api's strict pipe: an unknown key (`maxSizeGb`, an org id, an
* `exists`); a value outside its grammar or range; a body with no member, at the root, message
* `name at least one setting`. No `.transform()`.
*/
const updateBlobBucketRequestSchema = z.strictObject({
	location: dataLocationSchema.nullable().optional(),
	deleteAfterDays: z.number().int().min(1).max(BLOB_BUCKET_DELETE_AFTER_DAYS_MAX).nullable().optional(),
	removalGraceDays: z.number().int().min(1).max(BLOB_BUCKET_DELETE_AFTER_DAYS_MAX).optional()
}).refine((body) => Object.keys(body).length > 0, { message: "name at least one setting" });
/** The 200 body of {@link UPDATE_BLOB_BUCKET_ROUTE}: the bucket's row after the write. */
const updateBlobBucketResponseSchema = blobBucketSchema;
/**
* The body of {@link REQUEST_BLOB_BUCKET_RELOCATION_ROUTE}: `{ targetLocation }`, a single region
* (`dataLocationSchema` of `@zion/sdk-contracts`). The org comes from the principal, so the body
* names no org.
*
* Refusals, each a 400 from core-api's strict pipe: an unknown key (an org id, a `windowStart`, a
* `gate`); a missing `targetLocation`; a multi-region (`us`, `eu`) or a value outside the grammar.
* No `.transform()`.
*/
const requestBlobBucketRelocationRequestSchema = z.strictObject({ targetLocation: dataLocationSchema });
/** The 201 body of {@link REQUEST_BLOB_BUCKET_RELOCATION_ROUTE}: `{ requestId }`, the UUID core-api minted. */
const requestBlobBucketRelocationResponseSchema = z.object({ requestId: z.uuid() });
/**
* The refusal codes of the bucket location pick and the shared bucket consent at accept, and of
* {@link UPDATE_BLOB_BUCKET_ROUTE}, in the order they joined the contract:
*
* - `blob_bucket_not_found` (404, PATCH and the relocation request): the pair names no
*   allocatable bucket of an app the org accepted;
* - `blob_bucket_not_declared` (400, accept): a `bucketLocations` key is not a `scope: org`,
*   `locality: central` bucket of the app;
* - `blob_bucket_locations_unknown` (409, both): a location is named and the data plane holds no
*   allowed list yet. At accept it also answers when core-api cannot read the app's declarations,
*   the stored offer list names no `global` bucket, and the body does not hold
*   `sharedBucketConsent: true`, with or without a pick (R-094 item 6, R-096 item 2);
* - `blob_bucket_location_not_allowed` (400, both): a location outside the allowed list;
* - `blob_bucket_location_locked` (409, both): a location change on a bucket that exists;
* - `blob_bucket_retention_shorter` (400, PATCH): a `deleteAfterDays` shorter than the app's;
* - `blob_bucket_shared_consent_required` (400, accept): the app declares a `scope: global` bucket
*   and the accept body does not hold `sharedBucketConsent: true` (2026-10-13-blob-buckets R-094
*   item 6). The stored offer list of the app counts as a declaration (R-096 item 2), also when
*   core-api cannot read the manifest. core-api checks this code before the 409 below. An accept
*   that lacks the consent, whose declarations core-api cannot read, and whose stored list names no
*   `global` bucket answers 409 `blob_bucket_locations_unknown` in place of this code.
*/
const BLOB_BUCKET_ERROR_CODES = Object.freeze([
	"blob_bucket_not_found",
	"blob_bucket_not_declared",
	"blob_bucket_locations_unknown",
	"blob_bucket_location_not_allowed",
	"blob_bucket_location_locked",
	"blob_bucket_retention_shorter",
	"blob_bucket_shared_consent_required"
]);
/**
* The refusal codes of {@link REQUEST_BLOB_BUCKET_RELOCATION_ROUTE} beside the shared
* `blob_bucket_not_found` (R-089 item 2):
*
* - `relocation_bucket_not_created` (409): the bucket does not exist yet;
* - `relocation_location_not_allowed` (400): a target outside the allowed list;
* - `relocation_same_location` (409): the target is where the bucket lives;
* - `relocation_request_open` (409): the org and the app hold an open request or plan.
*/
const BLOB_BUCKET_RELOCATION_ERROR_CODES = Object.freeze([
	"relocation_bucket_not_created",
	"relocation_location_not_allowed",
	"relocation_same_location",
	"relocation_request_open"
]);
/**
* Every route this file declares, folded into `ADMIN_CONTRACT_ROUTE_FAMILIES` under
* `blob-buckets`: the list, then the update, then the relocation request.
*/
const blobBucketsRoutes = Object.freeze([
	LIST_BLOB_BUCKETS_ROUTE,
	UPDATE_BLOB_BUCKET_ROUTE,
	REQUEST_BLOB_BUCKET_RELOCATION_ROUTE
]);
//#endregion
export { ADMIN_BLOB_BUCKETS_PATH, ADMIN_BLOB_BUCKET_PATH_TEMPLATE, ADMIN_BLOB_BUCKET_RELOCATIONS_PATH_TEMPLATE, BLOB_BUCKET_ERROR_CODES, BLOB_BUCKET_RELOCATION_ERROR_CODES, LIST_BLOB_BUCKETS_ROUTE, REQUEST_BLOB_BUCKET_RELOCATION_ROUTE, UPDATE_BLOB_BUCKET_ROUTE, adminBlobBucketPath, adminBlobBucketRelocationsPath, blobBucketPathParamsSchema, blobBucketRelocationSchema, blobBucketSchema, blobBucketsRoutes, listBlobBucketsResponseSchema, requestBlobBucketRelocationRequestSchema, requestBlobBucketRelocationResponseSchema, updateBlobBucketRequestSchema, updateBlobBucketResponseSchema };

//# sourceMappingURL=blob-buckets.js.map